import { useState } from 'react';
import {
	DisciplineEnum,
	FrequencyCadenceTypeEnum,
	FrequencyCadenceTypeOptions,
	FrequencyStatusEnum,
	FrequencyTypeEnum,
	type FullOrderViewModel,
	type LabFrequencyCommandModel
} from '@generated';
import type { FormHelpers } from '~/shared/forms/types';
import { useAbbreviateFrequencies } from '../abbreviated/useAbbreviateFrequencies';
import { CertificationPeriodCalendar } from '~/features/scheduling/cert-periods/CertificationPeriodCalendar';
import { AbbreviatedFrequenciesTable } from '../abbreviated/AbbreviatedFrequenciesTable';
import { SelectField } from '~/shared/forms/controls/base-fields/SelectField';
import { convertDraftFrequenciesToCalendarEvent } from '../util';
import { CheckboxField } from '~/shared/forms/controls/base-fields/CheckboxField';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';

const removeLabFrequency = (id: string, formHelpers: FormHelpers) => {
	formHelpers.setFieldValue(
		'labFrequencies',
		(formHelpers.values.labFrequencies as LabFrequencyCommandModel[]).filter(lab => lab.id !== id)
	);
};

export function AddLabFrequenciesFormControl({ fullOrder, formHelpers }: { fullOrder: FullOrderViewModel; formHelpers: FormHelpers }) {
	const { loggedInUser } = useIdentity();
	const abbreviateFrequencies = useAbbreviateFrequencies();
	const [selectedCadenceTypeId, setSelectedCadenceTypeId] = useState<number>(FrequencyCadenceTypeEnum.Weekly);
	const [assignCurrentUserToNewFrequencies, setAssignCurrentUserToNewFrequencies] = useState(loggedInUser.clinicalTypeId ? true : false);

	const addLabFrequency = (scheduledDateAndTime: Date, formHelpers: FormHelpers) => {
		const newLabFrequency = {
			id: window.crypto.randomUUID(),
			scheduledDateAndTime,
			frequencyCadenceTypeId: selectedCadenceTypeId,
			assignedToId: loggedInUser.clinicalTypeDisciplineId && assignCurrentUserToNewFrequencies ? loggedInUser.id : undefined
		};
		formHelpers.setFieldValue('labFrequencies', [...(formHelpers.values.labFrequencies as LabFrequencyCommandModel[]), newLabFrequency]);
	};

	const onChangeAssignToCurrentUser = (selected: boolean) => {
		if (!loggedInUser.clinicalTypeDisciplineId) return;
		setAssignCurrentUserToNewFrequencies(selected);
		formHelpers.setFieldValue(
			'labFrequencies',
			(formHelpers.values.labFrequencies as LabFrequencyCommandModel[]).map(f => ({
				...f,
				assignedToId: selected ? loggedInUser.id : undefined
			}))
		);
	};

	const frequencies = formHelpers.values.labFrequencies as LabFrequencyCommandModel[];

	const events = convertDraftFrequenciesToCalendarEvent([
		...fullOrder.evaluations.map(f => ({ ...f, isReadOnly: true })),
		...fullOrder.routine.map(f => ({ ...f, isReadOnly: true })),
		...fullOrder.labs.map(f => ({ ...f, isReadOnly: true })),
		...frequencies.map(f => ({ ...f, frequencyTypeId: FrequencyTypeEnum.Lab, disciplineId: DisciplineEnum.SkilledNursing, isReadOnly: false }))
	]);
	const abbreviatedFrequencies = abbreviateFrequencies(
		frequencies.map(x => ({
			...x,
			frequencyTypeId: FrequencyTypeEnum.Lab,
			disciplineId: DisciplineEnum.SkilledNursing,
			frequencyStatusId: FrequencyStatusEnum.Drafted
		})),
		fullOrder.certificationPeriod
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between gap-2">
				<SelectField
					label="Frequency Cadence"
					density="compact"
					value={selectedCadenceTypeId}
					items={FrequencyCadenceTypeOptions}
					onChange={value => setSelectedCadenceTypeId(Number(value!))}
					orientation="horizontal"
				/>
				<div className="flex items-center gap-2">
					{loggedInUser.clinicalTypeId && (
						<CheckboxField
							value={assignCurrentUserToNewFrequencies}
							onChange={selected => onChangeAssignToCurrentUser(selected)}
							label="Assign new frequencies to me"
							hideLabel
						/>
					)}
				</div>
			</div>

			<CertificationPeriodCalendar
				events={events}
				certificationPeriod={fullOrder.certificationPeriod}
				onCalendarCellClicked={date => addLabFrequency(date, formHelpers)}
				onCalendarItemClicked={lab => removeLabFrequency(lab.id, formHelpers)}
			/>

			<AbbreviatedFrequenciesTable rows={abbreviatedFrequencies} />
		</div>
	);
}
